import { useEffect, useState } from "react"
import { Plus, Calendar, Mail, Play, Pause, Trash2, Edit, Search, Send } from "lucide-react"
import CreateCampaignModal from "./CreateCampaign"
import EditCampaignModal from "./EditCampaignModal"
import services from "../../API"
import { useSelector } from "react-redux"
import { Loading } from "../Loader/Loading"
import {
    FormControl,
    Select,
    MenuItem,
} from "@mui/material"
import { toastify } from "../../components/Toast";
import ConfirmModal from "../Modals/ConfirmModal";


const Automation = () => {
    const campaignsData = useSelector((state) => state.data.apiData.getCampaigns?.data || []);
    const loading = useSelector((state) => state.data.loading.getCampaigns);
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [showEditModal, setShowEditModal] = useState(false)
    const [editingCampaign, setEditingCampaign] = useState(null)
    const [searchTerm, setSearchTerm] = useState("")
    const [statusFilter, setStatusFilter] = useState("all")
    const [selectedCampaign, setSelectedCampaign] = useState(null)
    const [activeTab, setActiveTab] = useState("list")
    const [campaignUpdate, setCampaignUpdate] = useState(false)
    const [campaignToDelete, setCampaignToDelete] = useState(null)
    const [isDeleting, setIsDeleting] = useState(false)



    const fetchCampaigns = async () => {
        try {
            await services.getCampaigns();
        } catch (error) {
            console.error("Error fetching campaigns:", error);
        }
    };

    useEffect(() => {
        fetchCampaigns()
    }, [campaignUpdate])


    const handleToggleStatus = async (id) => {
        try {
            await services.pauseResumeCampaign(id);
            fetchCampaigns()
        } catch (error) {
            console.error("Error pausing/resuming campaign:", error);
        }
    }

    const handleDeleteCampaign = (campaign) => {
        setCampaignToDelete(campaign)
    }

    const handleConfirmDeleteCampaign = async () => {
        if (!campaignToDelete?._id) return

        setIsDeleting(true)
        try {
            await services.deleteCampaign(campaignToDelete._id);
            await fetchCampaigns()
            if (selectedCampaign?._id === campaignToDelete._id) {
                setSelectedCampaign(null)
                setActiveTab("list")
            }
            setCampaignToDelete(null)
        } catch (error) {
            console.error("Error deleting campaign:", error);
        } finally {
            setIsDeleting(false)
        }
    }

    const handleCampaignCreated = (value) => {
        setCampaignUpdate(value)
    }





    const handleEditCampaign = (campaign) => {
        setEditingCampaign(campaign)
        setShowEditModal(true)
    }

    const filteredCampaigns = campaignsData?.campaigns?.filter((campaign) => {
        const matchesSearch =
            campaign?.name.toLowerCase().includes(searchTerm?.toLowerCase()) ||
            campaign?.brandId?.brandName.toLowerCase().includes(searchTerm?.toLowerCase())
        const matchesStatus = statusFilter === "all" || (statusFilter === "active" && campaign.isActive) || (statusFilter === "paused" && !campaign.isActive)
        return matchesSearch && matchesStatus
    })

    function formatDate(isoString) {
        const date = new Date(isoString);

        return date.toLocaleString("en-US", {
            weekday: "short",   // e.g. "Wed"
            year: "numeric",    // e.g. "2025"
            month: "short",     // e.g. "Sep"
            day: "numeric",     // e.g. "24"
            hour: "2-digit",    // e.g. "08 PM"
            minute: "2-digit",  // e.g. "20"
            second: "2-digit",  // e.g. "19"
        });
    }
    function formatTimestamp(isoString, timezone = "UTC") {
        const date = new Date(isoString);

        return new Intl.DateTimeFormat("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            timeZone: timezone,
        }).format(date);
    }

    const CampaignTesting = async (id) => {
        try {
            toastify("success", "Email will be sent shortly to your inbox.");
            await services.testCampaign(id)
        } catch (error) {
            console.error(error);
        }
    };


    return (
        <div className="responsive-page responsive-page--automation min-w-0 flex-1 h-screen bg-white dark:bg-[#0A0A0A] w-full pb-5">
            <div className="pb-[20px]">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-[#fff]">Autopilot</h1>
                        <p className={`text-base mt-2 text-gray-600 dark:text-gray-400`}>
                            Get new ads and content created for you — automatically, on your schedule.
                        </p>
                        <p className={`text-sm mt-2 tracking-wide text-gray-400 dark:text-gray-500`}>
                            Set it once and we'll keep creating{" "}<span className="mx-1">·</span>{" "}Image ads run automatically{" "}<span className="mx-1">·</span>{" "}Video ads are guided in AI Marketing
                        </p>
                    </div>
                    <button
                        className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md flex items-center"
                        onClick={() => setShowCreateModal(true)}
                    >

                        <Plus className="w-4 h-4 mr-2" /> New Autopilot
                    </button>
                </div>
                {loading ? <Loading /> : (
                    <>

                        {/* Stats */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                            <div className="p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0A0A0A]">
                                <div className="flex items-center gap-2">
                                    <Calendar className="w-5 h-5 text-orange-500" />
                                    <div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Active Campaigns</p>
                                        <p className="text-2xl font-bold text-gray-900 dark:text-[#fff]">{campaignsData?.stats?.activeCampaigns}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0A0A0A]">
                                <div className="flex items-center gap-2">
                                    <Mail className="w-5 h-5 text-blue-500" />
                                    <div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Total Sent</p>
                                        <p className="text-2xl font-bold text-gray-900 dark:text-[#fff]">{campaignsData?.stats?.totalSent}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0A0A0A]">
                                <div className="flex items-center gap-2">
                                    <Pause className="w-5 h-5 text-gray-500" />
                                    <div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Paused</p>
                                        <p className="text-2xl font-bold text-gray-900 dark:text-[#fff]">{campaignsData?.stats?.pausedCampaigns}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Search + Filter */}
                        <div className="flex min-w-0 flex-wrap gap-4 mb-6">
                            <div className="relative min-w-0 flex-1 basis-full sm:basis-auto">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    placeholder="Search campaigns..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                                />
                            </div>
                            <FormControl className="automation-status-filter" sx={{ minWidth: 200 }}>
                                <Select
                                    labelId="status-label"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    size="small"
                                    sx={{
                                        borderRadius: 2,
                                        backgroundColor: 'white',
                                        '&.MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: '#d1d5db',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: '#9ca3af',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#f97316',
                                            },
                                        },
                                        '@media (prefers-color-scheme: dark)': {
                                            backgroundColor: '#1f2937',
                                            color: 'white',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#4b5563',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#6b7280',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#f97316',
                                            },
                                            '& .MuiSvgIcon-root': {
                                                color: '#9ca3af',
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="all">
                                        All Campaigns
                                    </MenuItem>
                                    <MenuItem value="active">
                                        Active Only
                                    </MenuItem>
                                    <MenuItem value="paused">
                                        Paused Only
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </div>

                        {/* Tabs */}
                        <div className="space-y-4">
                            <div className="inline-flex h-10 items-center rounded-md bg-gray-100 dark:bg-gray-800 px-1 py-6">
                                <button
                                    onClick={() => setActiveTab("list")}
                                    className={`px-4 py-2 text-sm font-medium rounded-lg ${activeTab === "list" ? "bg-orange-500 text-white shadow" : "text-gray-600 dark:text-gray-400"
                                        }`}
                                >
                                    Campaign List
                                </button>

                                {selectedCampaign && (
                                    <button
                                        onClick={() => setActiveTab("details")}
                                        className={`px-4 py-2 text-sm font-medium rounded-lg ${activeTab === "details" ? "bg-orange-500 text-white shadow" : "text-gray-600 dark:text-gray-400"
                                            }`}
                                    >
                                        Campaign Details
                                    </button>
                                )}
                            </div>

                            {/* Tab Content */}
                            {activeTab === "list" && (
                                <div className="space-y-4">
                                    <h2 className="text-xl font-semibold text-gray-900 dark:text-[#fff]">Scheduled Campaigns</h2>
                                    {filteredCampaigns?.length === 0 ? (
                                        <div className="p-8 border border-gray-200 dark:border-gray-700 rounded-lg text-center bg-white dark:bg-[#0A0A0A]">
                                            <Calendar className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                                            <h3 className="text-lg font-medium text-gray-900 dark:text-[#fff] mb-2">
                                                {campaignsData.length !== 0 ? "No runs yet" : "No campaigns match your search"}
                                            </h3>
                                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                                {campaignsData.length !== 0
                                                    ? "Turn on Autopilot and start receiving new content — without lifting a finger."
                                                    : "Try adjusting your search or filter criteria"}
                                            </p>
                                            {campaignsData.length !== 0 && (
                                                <button
                                                    className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md flex items-center mx-auto"
                                                    onClick={() => setShowCreateModal(true)}
                                                >
                                                    <Plus className="w-4 h-4 mr-2" /> New Autopilot
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        filteredCampaigns?.map((campaign) => (
                                            <div
                                                key={campaign?._id}
                                                className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md transition-shadow bg-white dark:bg-[#0A0A0A]"
                                            >
                                                <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                                    <div
                                                        onClick={() => { setSelectedCampaign(campaign); setActiveTab("details") }}
                                                        className="min-w-0 flex-1 cursor-pointer"
                                                    >
                                                        <h3 className="text-lg font-medium text-gray-900 dark:text-[#fff]">{campaign.name}</h3>
                                                        <p className="text-gray-600 dark:text-gray-400">
                                                            {campaign?.brandId?.brandName} • {campaign.contentType} • Created {formatDate(campaign?.createdAt)}
                                                        </p>
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-1 sm:flex-nowrap sm:gap-2">
                                                        <span
                                                            className={`shrink-0 whitespace-nowrap px-2 py-1 text-xs rounded-full ${campaign?.isActive
                                                                ? "bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400"
                                                                : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                                                }`}
                                                        >
                                                            {campaign?.isActive ? "Active" : "Paused"}
                                                        </span>
                                                        <div className="relative group inline-block">
                                                            <button
                                                                type="button"
                                                                aria-label={`Send ${campaign.name} now`}
                                                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded text-gray-600 dark:text-gray-400"
                                                                onClick={() => CampaignTesting(campaign?._id)}
                                                            >
                                                                <Send className="w-4 h-4" />
                                                            </button>
                                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden sm:group-hover:block">
                                                                <div className="relative bg-black dark:bg-gray-700 text-white dark:text-gray-200 text-xs rounded px-3 py-1 whitespace-nowrap">
                                                                    Send Now
                                                                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-8 border-x-transparent border-t-8 border-t-black dark:border-t-gray-700"></div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="relative group inline-block">
                                                            <button
                                                                type="button"
                                                                aria-label={`Edit ${campaign.name}`}
                                                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded text-gray-600 dark:text-gray-400"
                                                                onClick={() => handleEditCampaign(campaign)}
                                                            >
                                                                <Edit className="w-4 h-4" />
                                                            </button>
                                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden sm:group-hover:block">
                                                                <div className="relative bg-black dark:bg-gray-700 text-white dark:text-gray-200 text-xs rounded px-3 py-1 whitespace-nowrap">
                                                                    Edit
                                                                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-8 border-x-transparent border-t-8 border-t-black dark:border-t-gray-700"></div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="relative group inline-block">
                                                            <button
                                                                type="button"
                                                                aria-label={`${campaign?.isActive ? "Pause" : "Resume"} ${campaign.name}`}
                                                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded text-gray-600 dark:text-gray-400"
                                                                onClick={() => handleToggleStatus(campaign?._id)}
                                                            >
                                                                {campaign?.isActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                                                            </button>
                                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden sm:group-hover:block">
                                                                <div className="relative bg-black dark:bg-gray-700 text-white dark:text-gray-200 text-xs rounded px-3 py-1 whitespace-nowrap">
                                                                    {campaign?.isActive ? "Pause" : "Resume"}
                                                                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-8 border-x-transparent border-t-8 border-t-black dark:border-t-gray-700"></div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="relative group inline-block">
                                                            <button
                                                                type="button"
                                                                aria-label={`Delete ${campaign.name}`}
                                                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded text-red-600 dark:text-red-400"
                                                                onClick={() => handleDeleteCampaign(campaign)}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden sm:group-hover:block">
                                                                <div className="relative bg-black dark:bg-gray-700 text-white dark:text-gray-200 text-xs rounded px-3 py-1 whitespace-nowrap">
                                                                    Delete
                                                                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-8 border-x-transparent border-t-8 border-t-black dark:border-t-gray-700"></div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 text-sm mt-4">
                                                    <div>
                                                        <p className="text-gray-600 dark:text-gray-400">Schedule</p>
                                                        <p className="font-bold text-gray-900 dark:text-[#fff]">{campaign?.scheduleAtFormatted}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-gray-600 dark:text-gray-400">Next Run</p>
                                                        <p className="font-bold text-gray-900 dark:text-[#fff]">{campaign?.nextRun ? formatTimestamp(campaign.nextRun, campaign.timezone || "America/Los_Angeles") : "Paused"}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-gray-600 dark:text-gray-400">Total Sent</p>
                                                        <p className="font-bold text-gray-900 dark:text-[#fff]">{campaign?.totalDeliveries} emails</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}

                            {activeTab === "details" && selectedCampaign && (
                                <div className="space-y-4">
                                    <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
                                        <h2 className="min-w-0 text-xl font-semibold text-gray-900 dark:text-[#fff]">{selectedCampaign.name}</h2>
                                        <button
                                            className="shrink-0 whitespace-nowrap border-2 border-gray-300 dark:border-gray-600 px-3 py-1 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                                            onClick={() => {
                                                setSelectedCampaign(null)
                                                setActiveTab("list")
                                            }}
                                        >
                                            Back to List
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg space-y-4 bg-white dark:bg-[#0A0A0A]">
                                            <h3 className="font-bold text-lg text-gray-900 dark:text-[#fff]">Campaign Details</h3>
                                            <div><span className="text-gray-600 dark:text-gray-400">Brand:</span><p className="font-bold text-gray-900 dark:text-[#fff]">{selectedCampaign?.brandId?.brandName}</p></div>
                                            <div><span className="text-gray-600 dark:text-gray-400">Content Type:</span><p className="font-bold text-gray-900 dark:text-[#fff]">{selectedCampaign.contentType}</p></div>
                                            <div><span className="text-gray-600 dark:text-gray-400">Schedule:</span><p className="font-bold text-gray-900 dark:text-[#fff]">{selectedCampaign.scheduleAtFormatted}</p></div>
                                            <div><span className="text-gray-600 dark:text-gray-400">Delivery Email:</span><p className="break-all font-bold text-gray-900 dark:text-[#fff]">{selectedCampaign.email}</p></div>
                                            <div>
                                                <span className="mb-1 block text-gray-600 dark:text-gray-400">Status:</span>
                                                <span
                                                    className={`inline-flex whitespace-nowrap px-3 py-1 font-bold text-sm rounded-full ${selectedCampaign?.isActive
                                                        ? "bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400"
                                                        : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                                        }`}
                                                >
                                                    {selectedCampaign.isActive ? "Active" : "Paused"}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg space-y-4 bg-white dark:bg-[#0A0A0A]">
                                            <h3 className="font-bold text-lg text-gray-900 dark:text-[#fff]">Performance Metrics</h3>
                                            <div><span className="text-gray-600 dark:text-gray-400">Total Executions:</span><p className="font-bold text-gray-900 dark:text-[#fff]">{selectedCampaign?.totalDeliveries}</p></div>
                                            <div><span className="text-gray-600 dark:text-gray-400">Last Run:</span><p className="font-bold text-gray-900 dark:text-[#fff]">{selectedCampaign?.lastDeliveryDate ? formatTimestamp(selectedCampaign?.lastDeliveryDate) : "N/A"}</p></div>
                                            <div><span className="text-gray-600 dark:text-gray-400">Next Run:</span><p className="font-bold text-gray-900 dark:text-[#fff]">{selectedCampaign?.nextRun ? formatTimestamp(selectedCampaign.nextRun, selectedCampaign.timezone || "America/Los_Angeles") : "Paused"}</p></div>
                                        </div>
                                    </div>

                                    {/* <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0A0A0A]">
                <h3 className="font-semibold mb-4 text-gray-900 dark:text-[#fff]">Execution History</h3>
                <div className="space-y-3">
                  {selectedCampaign.executions.map((exec, i) => (
                    <div
                      key={i}
                      className="p-3 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800 flex justify-between items-center"
                    >
                      <div>
                        <p className="font-medium text-gray-900 dark:text-[#fff]">{exec.date}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {exec.contentGenerated || "No content generated"}
                        </p>
                        {exec.error && <p className="text-sm text-red-600 dark:text-red-400">{exec.error}</p>}
                      </div>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${exec.status === "success"
                          ? "bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400"
                          : "bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400"
                          }`}
                      >
                        {exec.status === "success" ? "Success" : "Failed"}
                      </span>
                    </div>
                  ))}
                </div>
              </div> */}
                                </div>
                            )}
                        </div>
                    </>
                )}

            </div>

            <CreateCampaignModal
                open={showCreateModal}
                onOpenChange={setShowCreateModal}
                onCampaignCreated={handleCampaignCreated}
                prefilledData={false}
            />
            {/* {editingCampaign && ( */}
            <EditCampaignModal
                open={showEditModal}
                onOpenChange={setShowEditModal}
                campaign={editingCampaign}
                onCampaignUpdated={handleCampaignCreated}
            />
            {/* )} */}
            <ConfirmModal
                isOpen={Boolean(campaignToDelete)}
                onClose={() => setCampaignToDelete(null)}
                title="Delete Automation"
                message={`Are you sure you want to delete "${campaignToDelete?.name || "this automation"}"? This action cannot be undone.`}
                confirmLabel="Yes"
                cancelLabel="No"
                onConfirm={handleConfirmDeleteCampaign}
                onCancel={() => setCampaignToDelete(null)}
                isLoading={isDeleting}
            />
        </div>
    )
}

export default Automation
